import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Reveal, ProtectedImg, Eyebrow, SectionTitle, Glass, Disclaimer, SectionShell, PINK, PURPLE, GOLD } from './shared';

export const Hero = () => (
  <section id="vision" data-college-section="vision" className="relative min-h-screen flex items-end overflow-hidden" data-testid="college-hero">
    <div className="absolute inset-0 overflow-hidden">
      <div className="college-kenburns absolute inset-0">
        <ProtectedImg name="campus-exterior.jpg" alt="Illustrative architectural concept of the proposed UPA College creative campus at dusk" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.25) 40%, rgba(5,5,5,0.92) 85%, #050505 100%)' }} />
    </div>
    <div className="relative px-6 sm:px-10 lg:px-24 pb-20 pt-40 max-w-[1400px] mx-auto w-full">
      <Reveal>
        <p className="text-white/80 text-sm sm:text-base uppercase tracking-[0.35em] mb-4">Universal Performing Arts</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.05] max-w-4xl">
          THE CREATIVE<br />INDUSTRIES <span className="college-shimmer">COLLEGE</span>
        </h1>
        <p className="mt-5 text-lg sm:text-xl font-semibold tracking-wide college-shimmer-pink">Perform. Produce. Create. Innovate.</p>
        <p className="mt-6 text-white/70 text-base max-w-2xl leading-relaxed">
          An ambitious new vision for creative industries education, professional production and artistic collaboration in Milton Keynes.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <button data-testid="college-explore-btn" onClick={() => document.getElementById('opportunity')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 rounded-full font-bold text-white transition-transform hover:scale-105"
            style={{ background: 'linear-gradient(90deg, #FF007F, #7000FF)' }}>
            Explore the Vision
          </button>
          <button data-testid="college-partner-btn" onClick={() => document.getElementById('partnerships')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 rounded-full font-bold text-white border border-white/30 hover:border-[#FFD700] hover:text-[#FFD700] transition-colors backdrop-blur">
            Become a Partner
          </button>
        </div>
        <p className="mt-8 text-white/45 text-xs">
          Strategic concept proposal. Subject to educational partnerships, feasibility, funding and relevant approvals.
        </p>
      </Reveal>
    </div>
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 college-float" aria-hidden="true"><ChevronDown /></div>
  </section>
);

const OPPORTUNITY = [
  ['Creative Education', 'Developing confident, versatile young people with transferable creative and professional skills.', PINK],
  ['Industry Pathways', 'Connecting education to opportunities in performance, film, technical production and creative enterprise.', PURPLE],
  ['Cultural Infrastructure', 'Developing adaptable spaces that can serve students, local artists, production companies and community organisations.', GOLD],
  ['Innovation & Technology', 'Combining traditional performance with digital storytelling, screen production and emerging creative technologies.', PINK],
];

export const Opportunity = () => (
  <SectionShell id="opportunity">
    <Reveal>
      <Eyebrow>The Opportunity</Eyebrow>
      <SectionTitle>Why Milton Keynes?</SectionTitle>
      <p className="text-white/65 max-w-3xl leading-relaxed mb-4">
        One connected creative industry. UPA College aims to complement existing provision in Milton Keynes by combining
        multiple areas of the creative industries — performance, film, technical production and creative enterprise —
        within a single connected educational experience.
      </p>
      <p className="text-white/65 max-w-3xl leading-relaxed">
        The concept is designed with the potential to align with Milton Keynes&rsquo; Creative and Cultural Strategy
        2027–2035 and the city&rsquo;s wider growth ambitions.
      </p>
    </Reveal>
    <div className="grid sm:grid-cols-2 gap-5 mt-12">
      {OPPORTUNITY.map(([title, body, color], i) => (
        <Reveal key={title} delay={i * 120}>
          <Glass className="h-full group hover:border-white/25 transition-colors">
            <div className="w-10 h-1 rounded-full mb-5" style={{ background: color }} />
            <h3 className="text-white font-heading text-xl mb-3">{title}</h3>
            <p className="text-white/60 text-sm leading-relaxed">{body}</p>
          </Glass>
        </Reveal>
      ))}
    </div>
    <Reveal>
      <Disclaimer>
        Reference to local strategy indicates potential alignment only. Milton Keynes City Council has not endorsed,
        approved or funded this proposal.
      </Disclaimer>
    </Reveal>
  </SectionShell>
);

const PATHWAYS = [
  { key: 'stage', name: 'Stage & Screen', color: PINK, skills: 'Acting, musical theatre, singing, dance, acting for camera and movement.', projects: 'Live productions, screen scenes, showcase performances and cross-pathway collaborations.', careers: 'Performer, actor-musician, screen actor, dance artist, theatre maker.' },
  { key: 'film', name: 'Film & Digital', color: PURPLE, skills: 'Cinematography, directing, editing, sound, digital storytelling and content production.', projects: 'Short films, documentary pieces, branded content and filmed stage productions.', careers: 'Filmmaker, editor, camera operator, content producer, sound recordist.' },
  { key: 'production', name: 'Live Production', color: GOLD, skills: 'Stage management, lighting, technical sound, scenic design and live events.', projects: 'Technical delivery of college productions, events and public showcases.', careers: 'Stage manager, lighting technician, live sound engineer, production crew.' },
  { key: 'enterprise', name: 'Creative Enterprise', color: '#3ED598', skills: 'Producing, marketing, event management, budgeting, intellectual property and entrepreneurship.', projects: 'Producing and promoting real productions, events, campaigns and creative businesses.', careers: 'Producer, events manager, marketing executive, creative entrepreneur.' },
];

export const Education = () => {
  const [open, setOpen] = useState('stage');
  const selected = PATHWAYS.find(p => p.key === open);
  return (
    <SectionShell id="education" data-testid="college-education">
      <Reveal>
        <Eyebrow color={PURPLE}>The Educational Model</Eyebrow>
        <SectionTitle>Breadth. Depth. Collaboration.</SectionTitle>
        <p className="text-white/65 max-w-3xl leading-relaxed">
          Every student begins with a shared <span className="text-white font-semibold">Universal Foundation</span> —
          then specialises through one of four connected pathways, continuing to create together across disciplines.
        </p>
        <p className="mt-4 text-lg font-heading" style={{ color: GOLD }}>Discover widely. Specialise purposefully. Create together.</p>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-2 gap-10 items-center">
        {/* Interactive diagram */}
        <Reveal>
          <div className="relative mx-auto w-full max-w-[420px] aspect-square" role="group" aria-label="Curriculum pathways diagram">
            <div className="absolute inset-0 rounded-full border border-white/10" aria-hidden="true" />
            <div className="college-orbit absolute inset-3 rounded-full border border-dashed border-[#7000FF]/40" aria-hidden="true" />
            <div className="college-orbit absolute inset-10 rounded-full border border-white/5" style={{ animationDirection: 'reverse', animationDuration: '95s' }} aria-hidden="true" />
            <div className="college-core-pulse absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 sm:w-44 sm:h-44 rounded-full flex items-center justify-center text-center p-4 border border-white/20"
              style={{ background: 'radial-gradient(circle at 30% 30%, rgba(112,0,255,0.45), rgba(5,5,5,0.9))' }}>
              <span className="text-white font-heading text-sm sm:text-base leading-tight">UNIVERSAL<br />FOUNDATION</span>
            </div>
            {PATHWAYS.map((p, i) => {
              const angle = (i * 90 - 45) * (Math.PI / 180);
              const x = 50 + 42 * Math.cos(angle);
              const y = 50 + 42 * Math.sin(angle);
              const isOpen = open === p.key;
              return (
                <button key={p.key} data-testid={`college-pathway-${p.key}`}
                  onClick={() => setOpen(p.key)}
                  aria-pressed={isOpen}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full px-3 py-2 sm:px-4 sm:py-3 text-[11px] sm:text-xs font-bold text-center leading-tight transition-all"
                  style={{
                    left: `${x}%`, top: `${y}%`,
                    background: isOpen ? p.color : 'rgba(255,255,255,0.06)',
                    color: isOpen ? (p.color === PURPLE ? '#FFFFFF' : '#050505') : 'white',
                    border: `1px solid ${isOpen ? p.color : 'rgba(255,255,255,0.2)'}`,
                    boxShadow: isOpen ? `0 0 30px ${p.color}66` : 'none',
                    transform: `translate(-50%, -50%) scale(${isOpen ? 1.08 : 1})`,
                  }}>
                  {p.name}
                </button>
              );
            })}
          </div>
          <p className="text-center text-white/40 text-xs mt-4">Select a pathway to explore skills, projects and career directions.</p>
        </Reveal>

        {/* Expanded pathway info */}
        <Reveal delay={100}>
          <Glass data-testid="college-pathway-detail">
            <div className="w-12 h-1.5 rounded-full mb-5" style={{ background: selected.color }} />
            <h3 className="font-heading text-2xl text-white mb-5">{selected.name}</h3>
            {[['Skills', selected.skills], ['Example projects', selected.projects], ['Career directions', selected.careers]].map(([k, v]) => (
              <div key={k} className="mb-4">
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: selected.color }}>{k}</p>
                <p className="text-white/70 text-sm leading-relaxed">{v}</p>
              </div>
            ))}
          </Glass>
          <Glass className="mt-5 border-dashed">
            <p className="text-white/70 text-sm"><span className="font-semibold" style={{ color: GOLD }}>Potential elective:</span> Circus &amp; Physical Performance — a possible additional specialism, subject to facilities, qualifications and safety requirements.</p>
          </Glass>
        </Reveal>
      </div>
      <Reveal>
        <Disclaimer>
          All proposed courses and qualifications are subject to approval and agreement with an accredited education partner.
        </Disclaimer>
      </Reveal>
    </SectionShell>
  );
};

const JOURNEY = [
  { year: 'YEAR ONE — DISCOVER & CREATE', color: PINK, terms: [
    ['Autumn', 'Discover', 'Experience different creative disciplines, explore professional roles and identify individual strengths.'],
    ['Spring', 'Create', 'Join cross-disciplinary teams to develop original creative projects.'],
    ['Summer', 'Deliver', 'Present work publicly, receive feedback and identify a preferred specialist pathway.'],
  ]},
  { year: 'YEAR TWO — SPECIALISE & LAUNCH', color: PURPLE, terms: [
    ['Autumn', 'Specialise', 'Develop deeper expertise in a chosen discipline while continuing collaborative training.'],
    ['Spring', 'Produce', 'Take responsibility for a professional-style creative project involving performers, technicians, filmmakers and producers.'],
    ['Summer', 'Launch', 'Complete a major project and graduate with a portfolio, showreel, production experience and potential industry progression opportunities.'],
  ]},
];

export const Journey = () => (
  <SectionShell id="journey" data-testid="college-journey">
    <Reveal>
      <Eyebrow color={GOLD}>The Student Experience</Eyebrow>
      <SectionTitle>A two-year journey with purpose.</SectionTitle>
      <p className="text-white/65 max-w-3xl leading-relaxed">
        A disciplined progression from exploration to professional-style production — connecting the educational
        journey directly with real creative work.
      </p>
    </Reveal>
    <div className="mt-14 space-y-14">
      {JOURNEY.map((y) => (
        <div key={y.year}>
          <Reveal><h3 className="font-heading text-xl sm:text-2xl mb-8" style={{ color: y.color }}>{y.year}</h3></Reveal>
          <div className="relative grid sm:grid-cols-3 gap-5">
            <div className="hidden sm:block absolute top-6 left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" aria-hidden="true" />
            {y.terms.map(([term, title, body], i) => (
              <Reveal key={term} delay={i * 150}>
                <div className="relative">
                  <div className="college-dot-pulse w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm mb-4 relative z-10 border"
                    style={{ background: '#0b0b10', borderColor: y.color, color: y.color }}>
                    {i + 1}
                  </div>
                  <p className="text-white/40 text-xs uppercase tracking-widest mb-1">{term}</p>
                  <h4 className="text-white font-heading text-lg mb-2">{title}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      ))}
    </div>
  </SectionShell>
);
